
import Vue from "vue";

import { authMapActions } from "@/store/modules/auth";
import { settingMapActions } from "@/store/modules/setting";
import { IPublicMessage } from "@/store/modules/setting/types";
import { appMapGetters } from "@/store/modules/app";

import { validateFields } from "@/helpers";
import { version } from "../../../package.json";
import { logoPath } from "@/helpers/generalHelpers";
import CallToAction from "./CallToAction.vue";
import CustomAlert from "@/components/CustomAlert/CustomAlert.vue";
import { getSystemFilesURL } from "@/helpers";

interface ILoginData {
  version: string;
  loggingIn: boolean;
  error: string;
  credentials: {
    email: string;
    password: string;
  };
  hasValidCredentials: boolean;
  redirectTo: string;
  isAnAgent?: boolean;
  primaryBackgroundImage: string;
  logoPath: string;
  bgPattern: string;
  publicMessage: string;
  showPublicMessage: boolean;
}

export default Vue.extend({
  name: "login",
  data(): ILoginData {
    return {
      version,
      loggingIn: false,
      error: "",
      credentials: {
        email: "",
        password: ""
      },
      hasValidCredentials: false,
      redirectTo: "",
      isAnAgent: false,
      primaryBackgroundImage: "",
      logoPath: "",
      bgPattern: "",
      publicMessage: "",
      showPublicMessage: false
    };
  },
  async mounted() {
    // Add a small delay to ensure DOM is fully ready
    await this.$nextTick();

    const emailFieldRef = this.$refs.field_email as any;
    this.redirectTo = this.$route.query.redirectFrom
      ? (this.$route.query.redirectFrom as string)
      : "/";
    if (emailFieldRef && emailFieldRef.$el) {
      const input = emailFieldRef.$el.querySelector("input");
      if (input) {
        input.focus();
      }
    }
    if (this.$route.query && this.$route.query.email) {
      this.credentials.email = this.$route.query.email as string;
    }
    this.fetchPublicMessage();
    try {
      const [
        primaryBackgroundImage,
        bgPattern,
        logoPathImage
      ] = await Promise.all([
        getSystemFilesURL("AdobeStock_2867459232"),
        getSystemFilesURL("bg-pattern1"),
        logoPath()
      ]);
      if (primaryBackgroundImage) {
        this.primaryBackgroundImage = primaryBackgroundImage;
      }
      if (bgPattern) {
        this.bgPattern = bgPattern;
      }
      if (logoPathImage) {
        this.logoPath = logoPathImage;
      }
    } catch (error) {
      this.$bugSnagClient.notify(error);
    }
  },
  watch: {
    $isLoggedIn: function(loggedIn) {
      this.$nextTick(function() {
        if (loggedIn) {
          this.$router.push("/").catch(() => {});
          // this.$gtag.event("login", { method: "app" });
        }
      });
    },
    $isLoginMfa: function(loginMfa) {
      this.$nextTick(function() {
        if (loginMfa) {
          this.$router.push("/mfa").catch(() => {});
        }
      });
    },
    "credentials.email": {
      handler() {
        this.updateIsValid();
      }
    },
    "credentials.password": {
      handler() {
        this.updateIsValid();
      }
    }
  },
  methods: {
    ...authMapActions(["login"]),
    ...settingMapActions(["getPublicMessage"]),
    async fetchPublicMessage(): Promise<void> {
      try {
        const response = await this.getPublicMessage();
        const data = ((response && response.data) || {}) as Partial<
          IPublicMessage
        >;
        // Only surface the message when it is both present and enabled.
        if (data.showPublicMessage && data.publicMessage) {
          this.publicMessage = data.publicMessage;
          this.showPublicMessage = true;
        }
      } catch (error) {
        // A missing/failed public message must never block login.
        this.$bugSnagClient.notify(error);
      }
    },
    async loginToApp(): Promise<void> {
      this.error = "";
      this.loggingIn = true;
      const loading = this.$loading({
        lock: true
      });
      try {
        const refs = this.$refs;
        const isValid = validateFields(refs);
        if (isValid) {
          const { email, password } = this.credentials;
          await this.login({ email, password });
        } else {
          this.error = "Please provide a username and password.";
        }
      } catch (e) {
        const { message, status } = e as any;
        this.error = message;
        if (status === 423) {
          this.$router.push({
            path: "/emergency-maintenance",
            query: { message: message }
          });
        } else if (status === 403) {
          this.$router.push({ path: "/403", query: { message: message } });
        }
        this.$log("login failed", "Error", {
          email: this.credentials.email,
          message: message
        });
        this.$bugSnagClient.notify(e);
      } finally {
        loading.close();
        this.loggingIn = false;
        // loginTimer.done();
      }
    },
    async loginFormOnEnterEmail() {
      await this.$nextTick();
      const passwordFieldRef = this.$refs.field_password as any;
      if (passwordFieldRef && passwordFieldRef.$el) {
        const passwordField = passwordFieldRef.$el.querySelector("input");
        if (this.credentials.email) {
          if (passwordField) passwordField.focus();
        }
      }
    },
    async loginFormOnEnterPassword() {
      await this.$nextTick();
      const emailFieldRef = this.$refs.field_email as any;
      if (emailFieldRef && emailFieldRef.$el) {
        const emailField = emailFieldRef.$el.querySelector("input");
        if (this.credentials.email && this.credentials.password) {
          await this.loginToApp();
        } else if (!this.credentials.email && this.credentials.password) {
          if (emailField) emailField.focus();
        }
      }
    },
    validateEmail(email: string): boolean {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email);
    },
    updateIsValid() {
      const emailValid = this.validateEmail(this.credentials.email);
      const passwordValid = this.credentials.password.length > 0;
      this.hasValidCredentials = emailValid && passwordValid;
      this.error = "";
    }
  },
  components: { CallToAction, CustomAlert },
  computed: {
    ...appMapGetters(["getAppViewSettings", "getAppTheme"]),
    isStaging(): boolean {
      return ["staging", "development"].includes(
        process?.env?.VUE_APP_ENV || ""
      );
    },
    getLoginPageBaseURL(): string {
      return process.env.VUE_APP_ENV === "production"
        ? "https://www.atlasgeneral.com/"
        : "/";
    }
  }
});
